import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PointWindgram } from '../api/types';
import { useI18n } from '../i18n/I18nContext';

/**
 * Il windgram di una cella disegnato nel telefono, dai dati di app_windgram.php.
 *
 * Il grafico lo fa rasp-windgram.js, lo stesso file del sito nuovo (public/js, copiato da
 * scripts/sync-windgram.sh): qui ci sono solo il contorno - scheda windgram / curva di
 * stato, i numeri dell'ora toccata, legenda e provenienza. La curva di stato non costa
 * niente in piu': la colonna della risposta ha gia' tutte le ore del giorno.
 */

interface WindgramViewProps {
  data: PointWindgram;
  name?: string;           // nome del sito, se si arriva da un sito in elenco
}

type Tab = 'windgram' | 'sounding';

/** Una voce di legenda: il segno vero del grafico, come sul sito. */
const LegendItem: React.FC<{ svg: string; text: string }> = ({ svg, text }) => (
  <span className="wg-leg-item">
    <svg viewBox="0 0 30 18" dangerouslySetInnerHTML={{ __html: svg }} />
    {text}
  </span>
);

const LEGEND_SVG = {
  cima: '<line x1="2" y1="9" x2="28" y2="9" stroke="#8b5a2b" stroke-width="3.5" stroke-dasharray="9 4 2 4"/>',
  base: '<g transform="translate(15,10) scale(.42)"><path d="M-18 8 C-26 8 -26 -3 -17 -4 C-17 -14 -3 -18 3 -10 ' +
        'C10 -16 20 -10 18 -2 C25 -1 25 8 17 8 Z" fill="#e6e6e6" stroke="#666" stroke-width="2.4"/></g>',
  torre: '<rect x="0" y="0" width="30" height="18" fill="#ffc700"/><polyline points="9,17 9,4 12,2 15,1 18,2 21,4 21,17" ' +
         'fill="none" stroke="#999" stroke-width="1.6" stroke-dasharray="3 2"/>',
  zero: '<rect x="0" y="0" width="30" height="18" fill="#72ff00"/><path d="M15 2L15 16M9 5.5L21 12.5M9 12.5L21 5.5" ' +
        'stroke="#fff" stroke-width="2" stroke-linecap="round"/>',
  vela: '<g fill="none" stroke="#f0f" stroke-width="2"><path d="M4 14 A11 9 0 0 1 26 14"/><path d="M4 14 A11 5 0 0 1 26 14"/></g>',
  nubi: '<rect x="0" y="0" width="30" height="18" fill="#ffc700"/><path d="M-4 4L10 18M2 0L20 18M10 0L28 18M18 0L32 14' +
        'M-4 14L10 0M2 18L20 0M10 18L28 0M18 18L32 4" stroke="#222" stroke-width=".8"/>',
  saturo: '<rect x="0" y="0" width="30" height="18" fill="#ffc700"/><path d="M0 3H30M0 6H30M0 9H30M0 12H30M0 15H30" ' +
          'stroke="#111" stroke-width=".7"/>',
};

const hh = (o: string) => o.slice(0, 2) + ':' + o.slice(2);
const m0 = (v: number | null | undefined, unit: string) =>
  v === null || v === undefined ? '—' : Math.round(v) + unit;

export const WindgramView: React.FC<WindgramViewProps> = ({ data, name }) => {
  const { t, language } = useI18n();
  const [tab, setTab] = useState<Tab>('windgram');
  const hours = useMemo(() => Object.keys(data.colonna ? data.colonna.ore : {}).sort(), [data]);

  /* Ora di partenza dei numeri e della curva di stato: l'ora attuale se si guarda oggi,
   * altrimenti mezzogiorno. */
  const initialHour = useMemo(() => {
    const now = new Date();
    const cur = String(now.getHours()).padStart(2, '0') + '00';
    if (data.giorno === 0 && hours.indexOf(cur) >= 0) return cur;
    return hours.indexOf('1200') >= 0 ? '1200' : hours[Math.floor(hours.length / 2)] || '';
  }, [data, hours]);
  const [hour, setHour] = useState(initialHour);
  useEffect(() => { setHour(initialHour); }, [initialHour]);

  const chartRef = useRef<HTMLDivElement>(null);
  const RW = window.RaspWindgram;

  const day = new Date(data.data + 'T12:00:00').toLocaleDateString(language === 'it' ? 'it-IT' : 'en-GB',
    { weekday: 'short', day: 'numeric', month: 'short' });
  const where = (name ? name : t('wg.point', { lat: data.punto[0].toFixed(3), lon: data.punto[1].toFixed(3) })) +
    ' (' + data.dominio + ' ' + data.i + '/' + data.j + ')';

  const svg = useMemo(() => {
    if (!RW) return '';
    try {
      if (tab === 'windgram') {
        return RW.svg(data, {
          titolo: where + '  ' + day,
          griglia: data.dominio,
          testi: {
            instabile: t('wg.instabile'), stabile: t('wg.stabile'), gradiente: t('wg.gradiente'),
            griglia: t('wg.griglia'), terreno: t('wg.terreno'),
          },
        });
      }
      return RW.sondaggio(data.colonna, hour, {
        titolo: where,
        sottotitolo: day + ' — ' + hh(hour),
        testi: { temperatura: t('wg.sondTemperatura'), legenda: t('wg.sondLegenda') },
      });
    } catch (e) {
      console.error('rasp-windgram:', e);
      return '';
    }
  }, [RW, data, tab, hour, where, day, t]);

  // la colonna dell'ora scelta, evidenziata nel grafico (come sul sito)
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.querySelectorAll('.v-prof-col').forEach((el) => {
      el.classList.toggle('scelta', (el as HTMLElement).dataset.ora === hour);
    });
  }, [svg, hour]);

  const onChartClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const col = (e.target as Element).closest('.v-prof-col') as HTMLElement | null;
    if (col && col.dataset.ora) setHour(col.dataset.ora);
  };

  const k = hours.indexOf(hour);
  const step = (d: number) => {
    const n = hours[k + d];
    if (n) setHour(n);
  };

  const r = data.dati[hour];
  const barbs = RW
    ? '<g transform="translate(8,16) scale(.42)" fill="#000" stroke="#000">' + RW.barba(15) + '</g>' +
      '<g transform="translate(23,16) scale(.42)" fill="#000" stroke="#000">' + RW.barba(50) + '</g>'
    : '';

  if (!RW || !svg) {
    return <div className="windgram-error"><p>{t('wg.loadError')}</p></div>;
  }

  return (
    <div className="wg-view">
      <div className="wg-tabs">
        <button className={`wg-tab ${tab === 'windgram' ? 'wg-tab-active' : ''}`} onClick={() => setTab('windgram')}>
          {t('wg.tabWindgram')}
        </button>
        <button className={`wg-tab ${tab === 'sounding' ? 'wg-tab-active' : ''}`} onClick={() => setTab('sounding')}>
          {t('wg.tabSounding')}
        </button>
      </div>

      {tab === 'sounding' && (
        <div className="wg-hours">
          <button onClick={() => step(-1)} disabled={k <= 0}>&#9664;</button>
          <b>{hh(hour)}</b>
          <button onClick={() => step(1)} disabled={k < 0 || k >= hours.length - 1}>&#9654;</button>
        </div>
      )}

      <div className="wg-chart" ref={chartRef} onClick={onChartClick} dangerouslySetInnerHTML={{ __html: svg }} />

      {tab === 'windgram' && (
        <>
          <div className="wg-detail">
            {r ? (
              <>
                <b>{hh(hour)}</b>{' '}
                {t('wg.detTop')} <b>{m0(r.hbl, ' m')}</b>{' · '}
                {r.cumuli ? <>{t('wg.detBase')} <b>{m0(r.zsfclcl, ' m')}</b></> : <span className="wg-no">{t('wg.detNoCu')}</span>}
                {r.sovrasviluppo && <> · <b>{t('wg.detOver')}</b></>}
                {' · '}{t('wg.detClouds')} <b>{m0(r.nubi, '%')}</b>
                {' · '}{r.mm && r.mm > 0.05 ? <>{t('wg.detRain')} <b>{r.mm} mm</b></> : <span className="wg-no">{t('wg.detNoRain')}</span>}
                {r.wstar !== undefined && r.wstar !== null && <> · {t('wg.detThermal')} <b>{r.wstar.toFixed(1)} m/s</b></>}
                {r.hglider !== undefined && r.hglider !== null && <> · {t('wg.detMaxAlt')} <b>{m0(r.hglider, ' m')}</b></>}
              </>
            ) : (
              <span className="wg-no">{t('wg.tapHour')}</span>
            )}
          </div>
          <div className="wg-legend">
            <LegendItem svg={LEGEND_SVG.cima} text={t('wg.cima')} />
            <LegendItem svg={LEGEND_SVG.base} text={t('wg.base')} />
            <LegendItem svg={LEGEND_SVG.torre} text={t('wg.torre')} />
            <LegendItem svg={LEGEND_SVG.zero} text={t('wg.zero')} />
            <LegendItem svg={LEGEND_SVG.vela} text={t('wg.vela')} />
            <LegendItem svg={LEGEND_SVG.nubi} text={t('wg.nubi')} />
            <LegendItem svg={LEGEND_SVG.saturo} text={t('wg.saturo')} />
            <LegendItem svg={barbs} text={t('wg.barbe')} />
          </div>
        </>
      )}

      <div className="wg-note">
        {t('wg.run')} {data.run}{data.elaborato ? ' — ' + t('wg.elaborato') + ' ' + data.elaborato : ''}
        {tab === 'windgram' ? ' — ' + t('wg.nota') : ''}
      </div>
    </div>
  );
};
